import { Button, Heading, Modal, Text } from "@ttc3k/trekker";
import { css } from "styled-system/css";
import { Center } from "styled-system/jsx";
import { useBookerContext, useCartContext } from "widgets/booker/contexts";
import { useTranslation } from "react-i18next";
import {
  HtmlContent,
  OrderSuccessIcon,
  WhereaboutsLogo,
} from "shared/components";
import { getMultilingualString } from "shared/utils";
import { EnumProductType } from "gql/generated";
import { useIsBookingRequest } from "widgets/booker/shared/hooks/useIsBookingRequest";
import { useIsInvoicedAfterService } from "widgets/booker/shared/hooks/useIsInvoicedAfterService";

/** Only allow link schemes that are safe to open from admin-provided config. */
const isSafeCtaUrl = (url: string) => /^(https?:\/\/|mailto:|tel:)/i.test(url);

type OrderSuccessPageProps = {
  open: boolean;
  onBackToSiteClick: () => void;
};

export const OrderSuccessPage = ({
  open,
  onBackToSiteClick,
}: OrderSuccessPageProps) => {
  const { onCloseBookerModal, product, booker, language } = useBookerContext();
  const { resetCartValuesToDefault } = useCartContext();
  const isBookingRequest = useIsBookingRequest();
  const { isInvoicedAfterService, isMonthlyStatement } =
    useIsInvoicedAfterService();

  const useFeeSuccessCopy =
    product?.type === EnumProductType.Fee ||
    product?.type === EnumProductType.RenewableFee;

  const handleClose = () => {
    resetCartValuesToDefault();
    onBackToSiteClick();
    onCloseBookerModal();
  };

  const completionScreen = booker?.completionScreen;
  const completionMessage = completionScreen?.message
    ? getMultilingualString(completionScreen.message, language)
    : "";
  // Admin content is normalized on save, but guard against empty-paragraph HTML.
  const hasCompletionMessage =
    completionMessage.replace(/<[^>]*>/g, "").trim().length > 0;
  const ctaLabel = completionScreen?.ctaLabel
    ? getMultilingualString(completionScreen.ctaLabel, language).trim()
    : "";
  const ctaUrl = completionScreen?.ctaUrl?.trim() ?? "";
  const hasCta = !!ctaLabel && !!ctaUrl && isSafeCtaUrl(ctaUrl);

  const handleCtaClick = () => {
    window.open(ctaUrl, "_blank", "noopener,noreferrer");
    handleClose();
  };

  const { t } = useTranslation("booker");

  return (
    <Modal
      open={open}
      placement={"rightPane"}
      backdropProps={{
        bg: "transparent",
      }}
      hideCloseTrigger
      portal
    >
      <Center
        bg={"ink.100"}
        h={"full"}
        w={"full"}
        px={"400"}
        flexDir={"column"}
        gap="300"
        textAlign={"center"}
      >
        <OrderSuccessIcon />
        <Center flexDir="column" gap="100">
          <Heading size="h5" color="text.dark">
            {t(
              isBookingRequest
                ? "ORDER_SUCCESS_PAGE.REQUEST_SENT_HEADING"
                : useFeeSuccessCopy
                  ? "ORDER_SUCCESS_PAGE.FEE_SUCCESS_HEADING"
                  : "ORDER_SUCCESS_PAGE.BOOKED",
            )}
          </Heading>
          <Text color="text.mid">
            {t(
              isBookingRequest
                ? "ORDER_SUCCESS_PAGE.REQUEST_SENT_CONFIRMATION"
                : isMonthlyStatement
                  ? "ORDER_SUCCESS_PAGE.STATEMENT_CONFIRMATION"
                  : isInvoicedAfterService
                    ? "ORDER_SUCCESS_PAGE.AFTER_SERVICE_CONFIRMATION"
                    : useFeeSuccessCopy
                      ? "ORDER_SUCCESS_PAGE.FEE_SUCCESS_CONFIRMATION"
                      : "ORDER_SUCCESS_PAGE.CONFIRMATION_EMAIL",
            )}
          </Text>
          {hasCompletionMessage && (
            <HtmlContent
              html={completionMessage}
              className={css({ color: "text.mid", textAlign: "center" })}
            />
          )}
          {hasCta && (
            <Button
              mt={"100"}
              mode={"dark"}
              onClick={handleCtaClick}
              type="button"
            >
              {ctaLabel}
            </Button>
          )}
          <Button
            mt={hasCta ? "0" : "100"}
            mode={"light"}
            onClick={handleClose}
            type="button"
          >
            {t("ORDER_SUCCESS_PAGE.BACK_TO_SITE")}
          </Button>
        </Center>
        <Center position="absolute" bottom="600" flexDir="column" gap={"100"}>
          <Text color="text.light" visual="smallRegular">
            {t("POWERED_BY")}
          </Text>
          <WhereaboutsLogo width={110} height="auto" />
        </Center>
      </Center>
    </Modal>
  );
};
